import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Slide from '@mui/material/Slide';
import {useTranslation} from "react-i18next";
import {Box, Grid, IconButton, Paper, Stack, Tab, Tabs, useTheme} from "@mui/material";
import AnnotatorSetList from "./AnnotatorSetList";
import {makeRequest} from "../../../../services/requestService";
import {API_URL} from "../../../../constants";
import {useHistory} from "react-router-dom";
import PivanTokenAnnotator from "./PivanTokenAnnotator";
import LabelList from "./LabelList";
import PopoverSearchInput from "../../../Search/PopoverSearchInput";
import {AnnotatorHeader} from "./AnnotatorHeader";
import PropTypes from "prop-types";
import {useSnackbar} from "notistack";
import CloseIcon from '@mui/icons-material/Close';
import {findUsedLabelsAndCategories, replaceLabelsInText} from "../utils";
import {atom, useRecoilState} from "recoil";
import {localStorageEffect} from "../../../../services/localStorage";

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} children={props.children}/>;
});

Annotator.propTypes = {
    open: PropTypes.bool.isRequired,
    textID: PropTypes.number.isRequired,
    onClose: PropTypes.func
}

Annotator.defaultProps = {
    open: false,
    onClose: () => {
    }
}

export const selectedSetState = atom({
    key: 'selectedSetState ', // unique ID (with respect to other atoms/selectors)
    default: [], // default value
    effects_UNSTABLE: [
        localStorageEffect('selectedSet'),
    ]
});

export default function Annotator({open, onClose, textID}) {
    const history = useHistory();
    const {t} = useTranslation("common");
    const {enqueueSnackbar} = useSnackbar();
    const theme = useTheme();
    const [text, setText] = React.useState(undefined);
    const [selectedSet, setSelectedSet] = useRecoilState(selectedSetState);
    const [selectedLabels, setSelectedLabels] = React.useState([]);
    const [selectedTokens, setSelectedTokens] = React.useState([]);
    const [usedLabels, setUsedLabels] = React.useState([]);
    const [usedSets, setUsedSets] = React.useState([]);
    const [tabValue, setTabValue] = React.useState(0);
    const [labelSearchValue, setLabelSearchValue] = React.useState("");

    const fetchText = async (textID) => {
        let response = await makeRequest(`${API_URL}/text/${textID}`);
        if (response.status === 200) {
            return Promise.resolve(response.data);
        } else if (response.status === 400 || response.status > 403) {
            if (response.status === 404)
                history.replace("/error/notfound");
            else
                history.push("/error");
        }
        return Promise.reject(new Error());
    }

    React.useEffect(() => {
        fetchText(textID).then((newText) => {
            setText(newText);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [textID]);

    // update selected set
    React.useEffect(() => {
        if (Array.isArray(selectedSet)) {
            let setsID = selectedSet.map(set => set.id);
            makeRequest(`${API_URL}/label/sets?requestedSet=${setsID.join("&requestedSet=")}`).then((response) => {
                if (response.status === 200 && Array.isArray(response.data)) {
                    setSelectedSet(response.data);
                }
            }).catch(() => {
                enqueueSnackbar("Can't update selected sets", {variant: "warning"});
            });
        }
    }, []);

    // download the labels and label sets used in the text
    React.useEffect(() => {
        if (text) {
            const labelsAndCategories = findUsedLabelsAndCategories(text);
            if (labelsAndCategories.categoriesID.size > 0) {
                let labels = new Set();
                let validID = [];
                labelsAndCategories.categoriesID.forEach((categoryID) => {
                    if (Number.isInteger(categoryID))
                        validID.push(categoryID);
                });
                if (validID.length > 0)
                    makeRequest(`${API_URL}/label/sets?requestedSet=${validID.join("&requestedSet=")}`).then((response) => {
                        if (response.status === 200 && Array.isArray(response.data)) {
                            // setSelectedSet(response.data);
                            setUsedSets(response.data);

                            // set used labels
                            response.data.forEach((category) => {
                                category.labels.forEach((label) => {
                                    let isInText = labelsAndCategories.labelsID.has(label.id);
                                    let isAlreadyAdded = labels.has(label);
                                    if (isInText && !isAlreadyAdded)
                                        labels.add(label);
                                });
                            });
                            setUsedLabels([...labels]);

                            // replace labels in text
                            replaceLabelsInText(text, [...labels]);

                        } else if (response.status === 400 || response.status > 403) {
                            if (response.status === 404)
                                history.replace("/error/notfound");
                            else
                                history.push("/error");
                        }
                    });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text]);

    const handleClose = () => {
        fetchText(textID).then((newText) => {
            setText(newText);
        });
        setSelectedTokens([]);
        setSelectedLabels([]);
        onClose();
    };

    const saveAnnotation = (tokenID, labelsID) => {
        makeRequest(`${API_URL}/apply/labels/to/token/${tokenID}?labelsId=${labelsID.join("&labelsId=")}`, 'PUT')
            .then((response) => {
                if (response.status === 202)
                    enqueueSnackbar("Not synchronized, please reload", {variant: "warning"});
            })
            .catch(() => enqueueSnackbar("Not saved", {variant: "error"}));
    }

    const deleteAnnotation = (tokenID, labelID) => {
        makeRequest(`${API_URL}/label/${labelID}/delete/to/token/${tokenID}`, 'PUT')
            .then((response) => {
                if (response.status === 202)
                    enqueueSnackbar("Not synchronized, please reload", {variant: "warning"});
            })
            .catch(() => enqueueSnackbar("Not deleted", {variant: "error"}));
    }

    const handleChangeSelectedLabelsUsedSetList = (newValue) => {
        newValue.forEach(label => {
            let isAlreadyUsed = selectedSet.find(ss => ss.id === label.collection);
            if (!isAlreadyUsed) {
                let set = usedSets.find(ss => ss.id === label.collection);
                if (set) {
                    setSelectedSet(prevState => [...prevState, set]);
                }
            }
        });
        handleChangeSelectedLabels(newValue);
    }

    const handleChangeSelectedLabels = (newValue) => {
        setSelectedLabels(newValue);
    }

    const handleChangeSelectedSet = (newValue) => {
        // unselect labels if the set is unselected
        let labels = [];
        newValue.forEach(set => {
            set.labels.forEach(label => {
                labels.push(label.id);
            });
        });
        let newSelectedLabels = selectedLabels.filter(sl => labels.includes(sl.id));
        setSelectedLabels(newSelectedLabels);
        setSelectedSet(newValue);
    }

    const handleAnnotationChange = (token, labels) => {
        saveAnnotation(token.id, labels.map(l => l.id));
        //re-compute usedLabels and usedSets
        let newUsedSets = usedSets;
        let newUsedLabels = usedLabels;
        selectedLabels.forEach(label => {
            let isAlreadyUsed = newUsedSets.find(us => us.id === label.collection);
            if (!Boolean(isAlreadyUsed)) {
                let set = selectedSet.find(ss => ss.id === label.collection);
                if (set) {
                    newUsedSets.push(set);
                }
            }
            isAlreadyUsed = newUsedLabels.find(ul => ul.id === label.id);
            if (!isAlreadyUsed)
                newUsedLabels.push(label);
        });
        setUsedSets(newUsedSets);
        setUsedLabels(newUsedLabels);
    }

    const handleChangeSelectedToken = (newValue) => {
        setSelectedTokens(newValue);
    }

    const handleDeleteLabel = (labelToDelete) => {
        if (selectedTokens.length <= 0) return;
        selectedTokens.forEach((token) => {
            if (token.labels.find(l => l.id === labelToDelete.id))
                deleteAnnotation(token.id, labelToDelete.id);
            token.labels = token.labels.filter(label => label.id !== labelToDelete.id);
        });
        //re-compute usedLabels and usedSets
        const newUsedSets = new Set();
        const newUsedLabels = new Set();
        text.lines.forEach((line) => {
            line.tokens.forEach((token) => {
                token.labels.forEach((label) => {
                    newUsedLabels.add(label);
                    newUsedSets.add(label.collection);
                });
            });
        });
        const sets = usedSets.filter(us => newUsedSets.has(us.id));
        setUsedSets(sets);
        setUsedLabels([...newUsedLabels]);
        setSelectedTokens(prevState => [...prevState]);
    }

    const handleContextMenu = (event) => {
        event.preventDefault();
        event.stopPropagation();
    }

    // left part of annotator
    const labelsSelection = (
        <Paper elevation={0} sx={{height: "100%", p: 1}}>
            <Stack height="100%">
                <Toolbar
                    sx={{
                        justifyContent: "space-between",
                        borderBottom: `solid 2px ${theme.palette.secondary.dark}`
                    }}>
                    <Typography variant="h6" color="textSecondary" fontWeight="bold" textAlign="center">
                        {t("main.components.annotator.labels.title")}
                    </Typography>
                    <PopoverSearchInput
                        value={labelSearchValue}
                        onChange={(event) => setLabelSearchValue(event.target.value)}
                        onSubmit={() => null}/>
                </Toolbar>
                <Tabs
                    value={tabValue}
                    onChange={(event, newValue) => setTabValue(newValue)}
                    aria-label="labels selection"
                    variant="scrollable"
                    sx={{
                        borderBottom: theme => `solid 1px ${theme.palette.divider}`,
                        mb: 1
                    }}
                >
                    <Tab label={t("main.components.annotator.labels.tabs.selected")}/>
                    <Tab label={t("main.components.annotator.labels.tabs.used")}/>
                    <Tab label={t("main.components.annotator.labels.tabs.usedLabels")}/>
                </Tabs>
                {tabValue === 0 && <LabelList
                    labelsSets={selectedSet}
                    selected={selectedLabels}
                    onChange={handleChangeSelectedLabels}
                    labelFilter={label => label.label.toLowerCase().includes(labelSearchValue.toLowerCase())}
                />}
                {tabValue === 1 && <LabelList
                    labelsSets={usedSets}
                    selected={selectedLabels}
                    onChange={handleChangeSelectedLabelsUsedSetList}
                    labelFilter={label => label.label.toLowerCase().includes(labelSearchValue.toLowerCase())}
                />}
                {tabValue === 2 && <LabelList
                    labelsSets={usedSets}
                    labelFilter={label => usedLabels.find(ul => ul.id === label.id) && label.label.toLowerCase().includes(labelSearchValue.toLowerCase())}
                    selected={selectedLabels}
                    onChange={handleChangeSelectedLabelsUsedSetList}
                />}
            </Stack>
        </Paper>
    );

    return (
        <Dialog
            fullScreen
            open={open}
            onClose={handleClose}
            TransitionComponent={Transition}
            onContextMenu={handleContextMenu}
        >
            <AppBar sx={{
                position: 'sticky',
                backgroundColor: theme.palette.appBar.main,
            }}>
                <Toolbar>
                    <Stack direction="row" flexGrow={1} alignItems="baseline">
                        <Typography sx={{ml: 2}} variant="h3" component="div">
                            {t('main.title')} -
                        </Typography>
                        <Typography sx={{ml: 2, fontSize: 38}} variant="h3" component="div">
                            {t("main.components.annotator.title").toUpperCase()}
                        </Typography>
                    </Stack>
                    <IconButton color="inherit" onClick={handleClose}>
                        <CloseIcon/>
                    </IconButton>
                </Toolbar>
            </AppBar>
            <Box flexGrow={1} bgcolor={theme.palette.background.default}>
                <Grid container sx={{height: "100%"}} pt={2} pr={2} pl={2} spacing={2}>
                    <Grid item xs={12} md={9} xl={9} display="flex">
                        <Stack spacing={2} display="flex" width="100%">
                            <AnnotatorHeader
                                selectedTokens={selectedTokens}
                                usedLabels={usedLabels}
                                onDelete={handleDeleteLabel}
                            />
                            <Box display="flex" flexGrow={1}>
                                <Grid container columnSpacing={2}>
                                    <Grid item xs={12} md={5} xl={4}>
                                        {labelsSelection}
                                    </Grid>
                                    <Grid item xs={12} md={7} xl={8}>
                                        <PivanTokenAnnotator
                                            text={text}
                                            labelsToApply={selectedLabels}
                                            onTokenSelection={handleChangeSelectedToken}
                                            onChange={handleAnnotationChange}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={3} xl={3} display="flex" flexDirection="column">
                        <Paper elevation={0} sx={{flexGrow: 1, p: 1, display: "flex", flexDirection: "column"}}>
                            <AnnotatorSetList selected={selectedSet} onChange={handleChangeSelectedSet}/>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        </Dialog>
    );
}
